import React, { useState, useCallback, useMemo } from 'react';
import { Checkbox, Icon } from '@ann/web-components';
import {
  CardContainer,
  CheckboxWrapper,
  ContentArea,
  HeaderRow,
  TitleArea,
  Title,
  CategoryBadge,
  PinButton,
  DescriptionArea,
  DescriptionText,
  ShowMoreButton,
  FooterRow,
  AuthorInfo,
  AuthorAvatar,
  AuthorName,
  MetaItem,
  Separator,
  RightMeta,
  MetaIconItem,
  ExpirationBadge,
  StatusBadge,
} from './NoteCard.styled';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

// Parse API date as UTC when it has no timezone (avoids "in 5 hours" for recent notes)
const parseNoteDate = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr);
  const hasTimezone = s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s);
  return hasTimezone ? dayjs(dateStr) : dayjs.utc(dateStr).local();
};

const MAX_DESCRIPTION_LENGTH = 200;

// Category badge colors - all with distinct colors
const CATEGORY_STYLES = {
  FINANCE: { bg: '#FEF3C7', color: '#D97706' },
  SERVICE: { bg: '#D1FAE5', color: '#059669' },
  SALES: { bg: '#DBEAFE', color: '#2563EB' },
  GENERAL: { bg: '#E0E7FF', color: '#4F46E5' },
  SUPPORT: { bg: '#FEE2E2', color: '#DC2626' },
  MARKETING: { bg: '#FCE7F3', color: '#DB2777' },
  HR: { bg: '#F3E8FF', color: '#9333EA' },
  OPERATIONS: { bg: '#CCFBF1', color: '#0D9488' },
  LEGAL: { bg: '#FED7AA', color: '#EA580C' },
  OTHER: { bg: '#F1F5F9', color: '#475569' },
};

// Status badge styles
const STATUS_STYLES = {
  URGENT: { bg: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' },
  OVERDUE: { bg: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' },
  ACTIVE: { bg: '#EFF6FF', color: '#3B82F6', borderColor: '#BFDBFE' },
  COMPLETED: { bg: '#F0FDF4', color: '#16A34A', borderColor: '#BBF7D0' },
  ARCHIVED: { bg: '#F9FAFB', color: '#6B7280', borderColor: '#E5E7EB' },
  PENDING: { bg: '#FFFBEB', color: '#D97706', borderColor: '#FDE68A' },
};

const NoteCard = ({
  note,
  isSelected = false,
  onSelect,
  onEdit,
  onPin,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    id,
    title,
    description,
    category,
    owner,
    createdAt,
    lastUpdated,
    isPinned,
    status,
    visibility,
    attachmentCount,
    linkCount,
    expiresAt,
    isTeamNote,
  } = note;

  const toggleExpand = useCallback((e) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  }, []);

  const handleCheckboxChange = useCallback(() => {
    onSelect?.(id);
  }, [id, onSelect]);

  const handleCardClick = useCallback(() => {
    onEdit?.(note);
  }, [note, onEdit]);

  const handlePinClick = useCallback((e) => {
    e.stopPropagation();
    onPin?.(id, !isPinned);
  }, [id, isPinned, onPin]);

  // Get category badge styles
  const categoryStyle = useMemo(() => {
    const cat = (category || 'GENERAL').toUpperCase();
    return CATEGORY_STYLES[cat] || CATEGORY_STYLES.GENERAL;
  }, [category]);

  // Get status badge styles
  const statusStyle = useMemo(() => {
    const stat = (status || 'ACTIVE').toUpperCase();
    return STATUS_STYLES[stat] || STATUS_STYLES.ACTIVE;
  }, [status]);

  // Check if description needs truncation
  const needsTruncation = description && description.length > MAX_DESCRIPTION_LENGTH;
  const displayDescription = isExpanded
    ? description
    : description?.slice(0, MAX_DESCRIPTION_LENGTH);

  // Format dates (parse as UTC when API sends without timezone to avoid "in 5 hours" for recent notes)
  const formattedCreatedAt = useMemo(() => {
    const d = parseNoteDate(createdAt);
    return d && d.isValid() ? d.fromNow() : '';
  }, [createdAt]);

  const formattedEditedAt = useMemo(() => {
    if (!lastUpdated) return null;
    const created = parseNoteDate(createdAt);
    const updated = parseNoteDate(lastUpdated);
    if (!created?.isValid() || !updated?.isValid()) return null;
    if (created.isSame(updated, 'minute')) return null;
    return `Edited ${updated.fromNow()}`;
  }, [lastUpdated, createdAt]);

  // Calculate expiration
  const expirationInfo = useMemo(() => {
    if (!expiresAt) return null;
    const expDate = parseNoteDate(expiresAt) || dayjs(expiresAt);
    const now = dayjs();
    const isExpired = expDate.isBefore(now);
    const diff = isExpired ? now.diff(expDate, 'day') : expDate.diff(now, 'day');
    
    return {
      isExpired,
      text: isExpired ? `Expired ${diff}d ago` : `Expires in ${diff}d`,
    };
  }, [expiresAt]);

  return (
    <CardContainer $selected={isSelected} onClick={handleCardClick}>
      {/* Checkbox */}
      <CheckboxWrapper onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={isSelected} onChange={handleCheckboxChange} />
      </CheckboxWrapper>

      {/* Main Content */}
      <ContentArea>
        {/* Header Row */}
        <HeaderRow>
          <TitleArea>
            <Title>{title || 'Untitled'}</Title>
            {category && (
              <CategoryBadge $bgColor={categoryStyle.bg} $color={categoryStyle.color}>
                {category.toUpperCase()}
              </CategoryBadge>
            )}
          </TitleArea>
          <PinButton $isPinned={isPinned} onClick={handlePinClick} title={isPinned ? 'Unpin' : 'Pin'}>
            <Icon name="star" width="18px" height="18px" />
          </PinButton>
        </HeaderRow>

        {/* Description */}
        {description && (
          <DescriptionArea>
            <DescriptionText>{displayDescription}</DescriptionText>
            {needsTruncation && (
              <ShowMoreButton onClick={toggleExpand}>
                {isExpanded ? 'Show less' : 'Show more'}
                <Icon name={isExpanded ? 'expandLess' : 'expandMore'} width="16px" height="16px" />
              </ShowMoreButton>
            )}
          </DescriptionArea>
        )}

        {/* Footer Row */}
        <FooterRow>
          {/* Author Info */}
          <AuthorInfo>
            <AuthorAvatar>
              {owner?.avatar ? (
                <img src={owner.avatar} alt={owner.name} />
              ) : (
                <Icon name="person" width="14px" height="14px" />
              )}
            </AuthorAvatar>
            <AuthorName>{owner?.name || 'Unknown'}</AuthorName>
          </AuthorInfo>

          {/* Created timestamp */}
          <MetaItem>{formattedCreatedAt}</MetaItem>

          {/* Edited timestamp */}
          {formattedEditedAt && (
            <>
              <Separator>·</Separator>
              <MetaItem>{formattedEditedAt}</MetaItem>
            </>
          )}

          {/* Right side meta */}
          <RightMeta>
            {/* Visibility/Team */}
            <MetaIconItem>
              <Icon name={isTeamNote || visibility === 'TEAM' ? 'group' : 'visibilityOff'} width="16px" height="16px" />
              {isTeamNote || visibility === 'TEAM' ? 'Team' : 'Private'}
            </MetaIconItem>

            {/* Attachments */}
            {attachmentCount > 0 && (
              <MetaIconItem>
                <Icon name="attachFile" width="16px" height="16px" />
                {attachmentCount}
              </MetaIconItem>
            )}

            {/* Links */}
            {linkCount > 0 && (
              <MetaIconItem>
                <Icon name="link" width="16px" height="16px" />
                {linkCount}
              </MetaIconItem>
            )}

            {/* Expiration */}
            {expirationInfo && (
              <ExpirationBadge $isExpired={expirationInfo.isExpired}>
                <Icon name="schedule" width="14px" height="14px" />
                {expirationInfo.text}
              </ExpirationBadge>
            )}

            {/* Status */}
            {status && (
              <StatusBadge
                $bgColor={statusStyle.bg}
                $color={statusStyle.color}
                $borderColor={statusStyle.borderColor}
              >
                <Icon name={status.toUpperCase() === 'URGENT' || status.toUpperCase() === 'OVERDUE' ? 'schedule' : 'radioButtonUnchecked'} width="12px" height="12px" />
                {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
              </StatusBadge>
            )}
          </RightMeta>
        </FooterRow>
      </ContentArea>
    </CardContainer>
  );
};

export default NoteCard;
